import * as React from 'react';

import { logException } from './exceptions';
import { Slide } from './slide/slide';
import { ISlide, SliderState, RefFunction, IContainerSize, IWaitingState } from './types';
import {
  getSlideStyles,
  getPrevSlideIndex,
  getNextSlideIndex,
  getCurrentSlide,
  getSlidesStyles,
  getOffsetForZoomed,
  jumpToSlide,
} from './utils';
import { getYOffsetHanlder } from './utils/get_y_offset';

function transitionEndStateCreator(state: SliderState) {
  // just typecheck
  if (state.action !== 'transition') {
    logException(new Error('transitionEnd should be fired only after transition'));

    return;
  }

  // tslint:disable-next-line:switch-default
  switch (state.transition.type) {
    case 'to-slide':
      return jumpToSlide(state, state.transition.slideNumber);
    case 'to-coords':
      return {
        action: 'waiting',
        transition: undefined,
        offsetCoords: state.transition.coords,
      } as IWaitingState;
  }
}

export function getSlide(
  state: SliderState,
  transitionEnd: (stateCreator: (state: SliderState) => SliderState | undefined) => void,
  setSlideSize: (slideIndex: number) => RefFunction,
  noInterface?: boolean,
) {
  return (slideStyle: React.CSSProperties, slideState: ISlide, slideIndex: number, name: string) => {
    const { source } = slideState;
    const {
      slide: { img, video },
    } = source;
    const canTransitionEnd = name === 'current';
    if (canTransitionEnd || (state.slides.length > 1 && state.action !== 'zoom')) {
      return (
        <div
          {...getSlideStyles(slideStyle, state)}
          key={(video || img) + name}
          onTransitionEnd={canTransitionEnd ? () => transitionEnd(transitionEndStateCreator) : undefined}
        >
          <Slide noInterface={noInterface} source={source} contentRef={setSlideSize(slideIndex)} />
        </div>
      );
    }

    return null;
  };
}

export function getSlides(
  state: SliderState,
  containerSize: IContainerSize | undefined,
  transitionEnd: (stateCreator: (state: SliderState) => SliderState) => void,
  setSlideSize: (slideIndex: number) => RefFunction,
  noInterface?: boolean,
) {
  const prevSlideIndex = getPrevSlideIndex(state);
  const nextSlideIndex = getNextSlideIndex(state);
  const currentSlideState = getCurrentSlide(state);
  const prevSlideState = state.slides[prevSlideIndex];
  const nextSlideState = state.slides[nextSlideIndex];
  const { prevSlideStyle, currentSlideStyle, nextSlideStyle } = getSlidesStyles(
    state,
    getYOffsetHanlder(state, containerSize),
    containerSize,
    getOffsetForZoomed(currentSlideState, containerSize),
  );
  const slideCreator = getSlide(state, transitionEnd, setSlideSize, noInterface);

  return (
    <>
      {slideCreator(prevSlideStyle, prevSlideState, prevSlideIndex, 'prev')}
      {slideCreator(currentSlideStyle, currentSlideState, state.currentSlideIndex, 'current')}
      {slideCreator(nextSlideStyle, nextSlideState, nextSlideIndex, 'next')}
    </>
  );
}
